import { ConfidenceMeter } from "@/components/ConfidenceMeter";
import { UserSearch, MapPinned, Clock, TrendingUp, ShieldAlert } from "lucide-react";

const MAP_BG = "https://images.pexels.com/photos/12534264/pexels-photo-12534264.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ENGINES = [
  { action: "profiling", name: "Suspect Behavioral Profiling", icon: UserSearch, tag: "The Who" },
  { action: "geo", name: "Geo-Pattern Analysis", icon: MapPinned, tag: "The Where" },
  { action: "temporal", name: "Temporal Pattern AI", icon: Clock, tag: "The When" },
  { action: "drift", name: "Behavioral Drift Tracking", icon: TrendingUp, tag: "The Trajectory" },
  { action: "bias", name: "AI Ethics & Bias Detection", icon: ShieldAlert, tag: "The Conscience" },
];

const RISK = { high: "text-monk-critical border-monk-critical/40 bg-monk-critical/10", medium: "text-monk-warn border-monk-warn/40 bg-monk-warn/10", low: "text-neutral-400 border-white/15 bg-white/5" };
const LOC_COLOR = { crime: "#FF3B30", sighting: "#FFB020", dump: "#FF3B30", residence_guess: "#007AFF" };

function Corroborated({ high }) {
  return high
    ? <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm border border-monk-blue/40 text-monk-blue bg-monk-blue/10">CORROBORATED · 3+ DISCIPLINES</span>
    : <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm border border-white/10 text-neutral-500">UNCORROBORATED</span>;
}

function MonkNote({ text }) {
  if (!text) return null;
  return <div className="flex items-start gap-2 border-l-2 border-monk-blue/50 pl-3 py-1 mb-3"><span className="overline text-monk-blue shrink-0 mt-0.5">Monk</span><p className="hard-data text-[13px] text-neutral-200 leading-relaxed">{text}</p></div>;
}

function Disciplines({ list }) {
  if (!list?.length) return null;
  return <div className="flex flex-wrap gap-1.5 mt-2">{list.map((d, i) => <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10 text-neutral-300">{d}</span>)}</div>;
}

function ProfilingView({ r }) {
  const p = r.offender_profile || {};
  return (
    <div className="space-y-3 text-[13px]">
      <MonkNote text={r.monk_note} />
      <p className="hard-data text-neutral-100 leading-relaxed">{p.summary}</p>
      <div className="grid grid-cols-2 gap-2">
        {[["Age", p.age_range], ["Sex", p.sex], ["Occupation", p.occupation_type], ["Victim link", p.relationship_to_victim], ["Organization", p.organization]].filter(x => x[1]).map(([k, v], i) => (
          <div key={i} className="bg-black/40 border border-white/10 rounded-sm px-2.5 py-1.5"><div className="overline">{k}</div><div className="hard-data text-neutral-200">{v}</div></div>
        ))}
      </div>
      {(r.motive_hypotheses || []).length > 0 && <div><div className="overline mb-1">Motive Hypotheses</div>{r.motive_hypotheses.map((m, i) => <div key={i} className="flex items-center gap-2 mb-1"><span className="flex-1 text-neutral-200">{m.motive}</span><div className="w-24"><ConfidenceMeter value={m.confidence} size="sm" /></div></div>)}</div>}
      {(r.behavioral_signatures || []).length > 0 && <div><div className="overline mb-1">Signatures</div>{r.behavioral_signatures.map((s, i) => <div key={i} className="mb-1"><span className="text-monk-blue font-mono text-[11px]">{s.signature}</span> <span className="hunch-data">— {s.evidence_basis}</span></div>)}</div>}
      <Disciplines list={r.corroborating_disciplines} />
    </div>
  );
}

function GeoView({ r }) {
  const locs = r.locations || [];
  const anchor = r.anchor_point;
  return (
    <div className="space-y-3 text-[13px]">
      <MonkNote text={r.monk_note} />
      <div className="relative w-full rounded-md overflow-hidden border border-white/10" style={{ aspectRatio: "16/9" }} data-testid="geo-map">
        <img src={MAP_BG} alt="geo profile" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-monk-bg/40" />
        {locs.map((l, i) => (
          <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{ left: `${l.x}%`, top: `${l.y}%` }}>
            <div className="w-2.5 h-2.5 rounded-full ring-2 ring-black" style={{ background: LOC_COLOR[l.type] || "#fff" }} />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 whitespace-nowrap text-[9px] font-mono bg-black/80 px-1.5 py-0.5 rounded-sm text-neutral-200 opacity-80">{l.label}</div>
          </div>
        ))}
        {anchor && (
          <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${anchor.x}%`, top: `${anchor.y}%` }}>
            <div className="w-4 h-4 rounded-full bg-monk-blue/30 ring-2 ring-monk-blue animate-pulse" />
            <div className="absolute left-5 top-1/2 -translate-y-1/2 whitespace-nowrap text-[9px] font-mono bg-monk-blue/20 border border-monk-blue/40 px-1.5 py-0.5 rounded-sm text-monk-blue">ANCHOR · {anchor.label}</div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-white/10 text-neutral-300">PATTERN: {(r.spatial_pattern || "unknown").toUpperCase()}</span>
      </div>
      {anchor?.reasoning && <p className="hunch-data">{anchor.reasoning}</p>}
      {r.hunting_ground && <div><div className="overline mb-1">Hunting Ground</div><p className="text-neutral-200">{r.hunting_ground}</p></div>}
      <Disciplines list={r.corroborating_disciplines} />
    </div>
  );
}

function TemporalView({ r }) {
  return (
    <div className="space-y-3 text-[13px]">
      <MonkNote text={r.monk_note} />
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${r.escalation === "escalating" ? RISK.high : "border-white/10 text-neutral-400"}`}>ESCALATION: {(r.escalation || "none").toUpperCase()}</span>
        {r.risk_window && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-monk-warn/40 text-monk-warn bg-monk-warn/10">NEXT WINDOW: {r.risk_window}</span>}
      </div>
      <div className="relative pl-4 border-l border-white/15 space-y-3">
        {(r.timeline || []).map((t, i) => (
          <div key={i} className="relative">
            <div className="absolute -left-[21px] top-1 w-2 h-2 rounded-full bg-monk-blue ring-2 ring-black" />
            <div className="font-mono text-[11px] text-monk-blue">{t.time}</div>
            <div className="hard-data text-neutral-100">{t.event}</div>
            <div className="hunch-data text-[12px]">{t.significance}</div>
          </div>
        ))}
      </div>
      {r.cadence && <div><div className="overline mb-1">Cadence</div><p className="text-neutral-200">{r.cadence}</p></div>}
      <Disciplines list={r.corroborating_disciplines} />
    </div>
  );
}

function DriftView({ r }) {
  return (
    <div className="space-y-3 text-[13px]">
      <MonkNote text={r.monk_note} />
      <div className="space-y-2">
        {(r.drift_points || []).map((d, i) => (
          <div key={i} className="bg-black/40 border border-white/10 rounded-sm p-2.5">
            <div className="overline mb-0.5">{d.phase}</div>
            <div className="hard-data text-neutral-100">{d.behavior}</div>
            {d.change_from_previous && <div className="hunch-data text-[12px] mt-0.5">Δ {d.change_from_previous}</div>}
          </div>
        ))}
      </div>
      {r.trajectory && <div><div className="overline mb-1">Trajectory</div><p className="text-neutral-200">{r.trajectory}</p></div>}
      {r.significance && <p className="text-monk-warn text-[12px]">{r.significance}</p>}
      <Disciplines list={r.corroborating_disciplines} />
    </div>
  );
}

function BiasView({ r }) {
  const hc = r.holistic_check || {};
  return (
    <div className="space-y-3 text-[13px]">
      <MonkNote text={r.monk_note} />
      {(r.bias_flags || []).map((b, i) => (
        <div key={i} className="flex items-start gap-2" data-testid="bias-flag">
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border shrink-0 ${RISK[b.risk_level] || RISK.low}`}>{(b.type || "").replace(/_/g, " ").toUpperCase()}</span>
          <span className="text-neutral-200">{b.where}</span>
        </div>
      ))}
      {(r.unexamined_alternatives || []).length > 0 && <div><div className="overline mb-1">Overlooked Alternatives</div><ul className="list-disc pl-4 space-y-0.5 hunch-data">{r.unexamined_alternatives.map((n, i) => <li key={i}>{n}</li>)}</ul></div>}
      {(r.evidence_gaps || []).length > 0 && <div><div className="overline mb-1">Evidence Gaps</div><ul className="list-disc pl-4 space-y-0.5 text-neutral-300">{r.evidence_gaps.map((n, i) => <li key={i}>{n}</li>)}</ul></div>}
      <div className={`text-[12px] font-mono px-2.5 py-1.5 rounded-sm border ${hc.meets_three_discipline_rule ? "border-monk-blue/40 text-monk-blue bg-monk-blue/10" : "border-monk-critical/40 text-monk-critical bg-monk-critical/10"}`}>
        HOLISTIC (§17): {hc.disciplines_engaged || 0} disciplines · {hc.meets_three_discipline_rule ? "MEETS 3-DISCIPLINE RULE" : "BELOW THRESHOLD"}
      </div>
      {hc.note && <p className="hunch-data">{hc.note}</p>}
    </div>
  );
}

const VIEWS = { profiling: ProfilingView, geo: GeoView, temporal: TemporalView, drift: DriftView, bias: BiasView };

function EngineCard({ engine, result, wide }) {
  const Icon = engine.icon;
  const View = VIEWS[engine.action];

  return (
    <div className={`bg-monk-panel border border-white/10 rounded-md flex flex-col ${wide ? "lg:col-span-2" : ""}`} data-testid={`engine-card-${engine.action}`}>
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <Icon className="w-4 h-4 text-monk-blue" />
        <div className="leading-none flex-1">
          <div className="font-heading font-700 text-sm tracking-tight">{engine.name}</div>
          <div className="overline mt-1">{engine.tag}</div>
        </div>
        {result?.confidence != null && <div className="w-20"><ConfidenceMeter value={result.confidence} /></div>}
        {"high_confidence" in (result || {}) && <Corroborated high={result.high_confidence} />}
        {!result && <span className="text-[9px] font-mono text-neutral-600 shrink-0" data-testid={`pending-${engine.action}`}>PENDING</span>}
      </div>
      <div className="p-4 flex-1">
        {result ? <View r={result} /> : <p className="hunch-data text-[13px]">Awaiting auto-analysis — Monk runs this automatically as evidence lands.</p>}
      </div>
    </div>
  );
}

export function BehavioralPanel({ caseData }) {
  const findings = caseData?.case_engines || {};
  return (
    <div className="mt-8" data-testid="behavioral-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="overline">Behavioral & Geographic Intelligence</div>
        <div className="h-px flex-1 bg-white/10" />
        <span className="font-heading font-700 text-sm tracking-tight text-neutral-400">The Who · The Where · The When</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EngineCard engine={ENGINES[0]} result={findings.profiling} wide />
        <EngineCard engine={ENGINES[1]} result={findings.geo} wide />
        <EngineCard engine={ENGINES[2]} result={findings.temporal} />
        <EngineCard engine={ENGINES[3]} result={findings.drift} />
        <EngineCard engine={ENGINES[4]} result={findings.bias} wide />
      </div>
    </div>
  );
}
